/**
 * Web Push helpers — konwersje VAPID, capability detection, subscribe/unsubscribe.
 * iOS PWA push wymaga Add to Home Screen + iOS 16.4+.
 */

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const arr = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i)
  return arr
}

export function arrayBufferToBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) return ''
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export interface PushCapability {
  supported: boolean
  iosStandaloneRequired: boolean
  permission: NotificationPermission
}

export function detectPushCapability(): PushCapability {
  const supported =
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent)
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  return {
    supported,
    iosStandaloneRequired: isIOS && !isStandalone,
    permission: supported ? Notification.permission : 'denied',
  }
}

export interface PushSubscriptionPayload {
  endpoint: string
  p256dhKey: string
  authKey: string
}

export async function subscribeToPush(
  vapidPublicKey: string,
): Promise<PushSubscriptionPayload> {
  const reg = await navigator.serviceWorker.ready
  const existing = await reg.pushManager.getSubscription()
  if (existing) await existing.unsubscribe()
  // Cast: TS 5.7+ lib.dom.d.ts wymaga BufferSource z ArrayBuffer (nie ArrayBufferLike),
  // ale pushManager.subscribe akceptuje Uint8Array w runtime — cast jest bezpieczny.
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
  })
  return {
    endpoint: sub.endpoint,
    p256dhKey: arrayBufferToBase64Url(sub.getKey('p256dh')),
    authKey: arrayBufferToBase64Url(sub.getKey('auth')),
  }
}

export async function unsubscribeFromPush(): Promise<string | null> {
  const reg = await navigator.serviceWorker.ready
  const sub = await reg.pushManager.getSubscription()
  if (!sub) return null
  const endpoint = sub.endpoint
  await sub.unsubscribe()
  return endpoint
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null
  const reg = await navigator.serviceWorker.ready
  return reg.pushManager.getSubscription()
}
